import { format } from 'date-fns';
import type { TPlantControlSchedulerRevision } from './schedule-plant-revision.schema.ts';
type Weekday =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday';

type ScheduleTimeSlot = {
  id: string;
  startTime: string;
  endTime: string;
  powerSetpoint: number;
  isActive?: boolean;
  validUntil?: string | null;
};

type LowercaseWeekday = Lowercase<Weekday>;

type SlotAddedAction = {
  day: LowercaseWeekday;
  type: 'slot_added';
  slot: ScheduleTimeSlot;
};

type SlotRemovedAction = {
  day: LowercaseWeekday;
  type: 'slot_removed';
  slot: ScheduleTimeSlot;
};

type SlotExpiredAction = {
  day: LowercaseWeekday;
  type: 'slot_expired';
  slot: ScheduleTimeSlot;
};

type SlotUpdatedAction = {
  day: LowercaseWeekday;
  type: 'slot_updated';
  slotId: string;
  changes: {
    startTime?: { from: string; to: string };
    endTime?: { from: string; to: string };
    powerSetpoint?: { from: number; to: number };
    isActive?: { from?: boolean; to?: boolean };
  };
};

export type SchedulerSlotAction =
  | SlotAddedAction
  | SlotRemovedAction
  | SlotExpiredAction
  | SlotUpdatedAction;
type SchemaAction = NonNullable<TPlantControlSchedulerRevision['actions']>[number];

const activeStateLabel = (value?: boolean): string =>
  value === undefined ? 'unknown' : value ? 'active' : 'inactive';

const formatTimestamp = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : format(date, 'MMM dd, yyyy HH:mm');
};

const expiredSinceLabel = (validUntil?: string | null): string => {
  if (!validUntil) return '';
  const expiresAt = new Date(validUntil);
  if (Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() >= Date.now()) return '';
  return ` (was expired since ${format(expiresAt, 'MMM dd, yyyy HH:mm')})`;
};

export function prettifySchedulerActions(
  actions?: SchemaAction[],
  snapshot?: TPlantControlSchedulerRevision['snapshot']
): string[] {
  if (!actions || actions?.length === 0) return [];

  const capitalizeDay = (day: LowercaseWeekday): Weekday =>
    (day.charAt(0).toUpperCase() + day.slice(1)) as Weekday;

  const lines: string[] = [];

  for (const action of actions) {
    if (action.type === 'slot_added') {
      const dayLabel = capitalizeDay(action.day);
      const timeLabel = `${action.slot.startTime}-${action.slot.endTime}`;
      lines.push(
        `${dayLabel}: Added slot ${timeLabel} with power setpoint ${action.slot.powerSetpoint} and marked as ${activeStateLabel(action.slot.isActive)}`
      );
      continue;
    }

    if (action.type === 'slot_removed') {
      const dayLabel = capitalizeDay(action.day);
      const timeLabel = `${action.slot.startTime}-${action.slot.endTime}`;
      lines.push(
        `${dayLabel}: Removed slot ${timeLabel} with power setpoint ${action.slot.powerSetpoint} and marked as ${activeStateLabel(action.slot.isActive)}${expiredSinceLabel(action.slot.validUntil)}`
      );
      continue;
    }

    if (action.type === 'slot_expired') {
      const dayLabel = capitalizeDay(action.day);
      const timeLabel = `${action.slot.startTime}-${action.slot.endTime}`;
      const expiredOn = formatTimestamp(action.slot.validUntil);
      const onLabel = expiredOn ? ` on ${expiredOn}` : '';
      lines.push(
        `${dayLabel}: Slot ${timeLabel} expired${onLabel} and auto-deactivated. Power setpoint: ${action.slot.powerSetpoint} kW`
      );
      continue;
    }

    if (action.type === 'slot_updated') {
      const dayLabel = capitalizeDay(action.day);
      const parts: string[] = [];
      if (action.changes.startTime || action.changes.endTime) {
        const fromStart = action.changes.startTime?.from ?? '';
        const fromEnd = action.changes.endTime?.from ?? '';
        const toStart = action.changes.startTime?.to ?? '';
        const toEnd = action.changes.endTime?.to ?? '';
        parts.push(`slot ${fromStart}-${fromEnd} to ${toStart}-${toEnd}`);
      } else {
        const dayKey = capitalizeDay(action.day);
        const slotsForDay = (
          snapshot?.weeklySchedule as Record<Weekday, ScheduleTimeSlot[]> | undefined
        )?.[dayKey as Weekday];
        const currentSlot = slotsForDay?.find((s) => s.id === action.slotId);
        const timeLabel = currentSlot ? `${currentSlot.startTime}-${currentSlot.endTime}` : '';
        parts.push(`slot ${timeLabel}`);
      }
      if (action.changes.powerSetpoint) {
        parts.push(
          `power setpoint changed from ${action.changes.powerSetpoint.from} to ${action.changes.powerSetpoint.to}`
        );
      }
      if (action.changes.isActive) {
        parts.push(
          `changed active state from ${activeStateLabel(action.changes.isActive.from)} to ${activeStateLabel(action.changes.isActive.to)}`
        );
      }
      const tail = parts.length > 0 ? ` ${parts.join(', ')}` : '';
      lines.push(`${dayLabel}: Updated${tail}`);
      continue;
    }

    if (action.type === 'name_updated') {
      const from = action.changes.from ?? '';
      const to = action.changes.to ?? '';
      if (from && to) {
        lines.push(`Name changed from "${from}" to "${to}"`);
      }
      if (!from && to) {
        lines.push(`Name changed to "${to}"`);
      }
      continue;
    }

    if (action.type === 'description_updated') {
      const from = action.changes.from ?? '';
      const to = action.changes.to ?? '';
      lines.push(`Description changed from "${from}" to "${to}"`);
      continue;
    }

    if (action.type === 'active_updated') {
      lines.push(
        `Changed active state from ${activeStateLabel(action.changes.from)} to ${activeStateLabel(action.changes.to)}`
      );
      continue;
    }
  }

  return lines;
}
